import React, { useState, useEffect } from 'react';
import Card from '../../../../shared/components/Card';
import usePageTranslation from '../../../../shared/hooks/usePageTranslation';

export interface AllianceSettingsData {
  name: string;
  slogan: string;
  presentationText: string;
  discordLink: string;
  rulesText: string;
  acceptanceRules: string;
  minLevel: number;
  allowBeginners: boolean;
}

interface AllianceParamsProps {
  initialData?: Partial<AllianceSettingsData>;
  isAdmin: boolean;
  onSave?: (data: AllianceSettingsData) => void;
  /** Noms déjà utilisés par d'autres alliances (minuscules) pour vérifier l'unicité */
  existingNames?: string[];
}

const FORBIDDEN_WORD = 'opsecours';

const defaultData: AllianceSettingsData = {
  name: '',
  slogan: '',
  presentationText: '',
  discordLink: '',
  rulesText: '',
  acceptanceRules: '',
  minLevel: 1,
  allowBeginners: true,
};

const AllianceParams: React.FC<AllianceParamsProps> = ({
  initialData,
  isAdmin,
  onSave,
  existingNames = [],
}) => {
  const { t } = usePageTranslation('game', 'AlliancePage');
  const [data, setData] = useState<AllianceSettingsData>({
    ...defaultData,
    ...initialData,
  });
  const [saved, setSaved] = useState(false);
  const [nameError, setNameError] = useState('');

  useEffect(() => {
    if (initialData && Object.keys(initialData).length > 0) {
      setData((prev) => ({ ...defaultData, ...prev, ...initialData }));
    }
  }, [initialData]);

  const handleChange = (field: keyof AllianceSettingsData, value: string | number | boolean) => {
    setData((prev) => ({ ...prev, [field]: value }));
    setSaved(false);
    if (field === 'name') setNameError('');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setNameError('');
    const trimmedName = (data.name || '').trim();
    const currentName = (initialData?.name ?? '').trim().toLowerCase();
    if (trimmedName.toLowerCase().includes(FORBIDDEN_WORD)) {
      setNameError(t('params.nameForbidden'));
      return;
    }
    const nameLower = trimmedName.toLowerCase();
    if (nameLower !== currentName && existingNames.includes(nameLower)) {
      setNameError(t('params.nameTaken'));
      return;
    }
    onSave?.(data);
    setSaved(true);
  };

  if (!isAdmin) {
    return (
      <Card className="p-6">
        <p className="text-gray-400">{t('params.adminOnly')}</p>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <h2 className="text-xl font-bold text-white mb-6">{t('params.title')}</h2>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            {t('params.allianceName')}
          </label>
          <input
            type="text"
            value={data.name}
            onChange={(e) => handleChange('name', e.target.value)}
            className={`w-full px-4 py-2 rounded-lg bg-gray-800 border text-white placeholder-gray-500 focus:ring-2 focus:ring-indigo-500 ${nameError ? 'border-red-500' : 'border-gray-600'}`}
            placeholder={t('params.allianceNamePlaceholder')}
          />
          {nameError && (
            <p className="mt-1.5 text-sm text-red-400" role="alert">{nameError}</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            {t('params.slogan')}
          </label>
          <input
            type="text"
            value={data.slogan}
            onChange={(e) => handleChange('slogan', e.target.value)}
            className="w-full px-4 py-2 rounded-lg bg-gray-800 border border-gray-600 text-white placeholder-gray-500 focus:ring-2 focus:ring-indigo-500"
            placeholder={t('params.sloganPlaceholder')}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            {t('params.discordLink')}
          </label>
          <input
            type="url"
            value={data.discordLink}
            onChange={(e) => handleChange('discordLink', e.target.value)}
            className="w-full px-4 py-2 rounded-lg bg-gray-800 border border-gray-600 text-white placeholder-gray-500 focus:ring-2 focus:ring-indigo-500"
            placeholder={t('params.discordLinkPlaceholder')}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            {t('params.presentationText')}
          </label>
          <textarea
            value={data.presentationText}
            onChange={(e) => handleChange('presentationText', e.target.value)}
            rows={4}
            className="w-full px-4 py-2 rounded-lg bg-gray-800 border border-gray-600 text-white placeholder-gray-500 focus:ring-2 focus:ring-indigo-500 resize-y"
            placeholder={t('params.presentationPlaceholder')}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            {t('params.minLevel')}
          </label>
          <input
            type="number"
            min={1}
            value={data.minLevel}
            onChange={(e) => handleChange('minLevel', Number(e.target.value))}
            className="w-full px-4 py-2 rounded-lg bg-gray-800 border border-gray-600 text-white focus:ring-2 focus:ring-indigo-500 max-w-xs"
          />
        </div>
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="allowBeginners"
            checked={data.allowBeginners}
            onChange={(e) => handleChange('allowBeginners', e.target.checked)}
            className="rounded bg-gray-800 border-gray-600 text-indigo-500 focus:ring-indigo-500"
          />
          <label htmlFor="allowBeginners" className="text-sm text-gray-300">
            {t('params.allowBeginners')}
          </label>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            {t('params.rulesText')}
          </label>
          <textarea
            value={data.rulesText}
            onChange={(e) => handleChange('rulesText', e.target.value)}
            rows={6}
            className="w-full px-4 py-2 rounded-lg bg-gray-800 border border-gray-600 text-white placeholder-gray-500 focus:ring-2 focus:ring-indigo-500 resize-y"
            placeholder={t('params.rulesTextPlaceholder')}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            {t('params.acceptanceRules')}
          </label>
          <textarea
            value={data.acceptanceRules}
            onChange={(e) => handleChange('acceptanceRules', e.target.value)}
            rows={3}
            className="w-full px-4 py-2 rounded-lg bg-gray-800 border border-gray-600 text-white placeholder-gray-500 focus:ring-2 focus:ring-indigo-500 resize-y"
            placeholder={t('params.acceptanceRulesPlaceholder')}
          />
        </div>
        <div className="flex items-center gap-4">
          <button
            type="submit"
            className="px-6 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-medium transition-colors"
          >
            {t('params.save')}
          </button>
          {saved && (
            <span className="text-sm text-green-400">{t('params.saved')}</span>
          )}
        </div>
      </form>
    </Card>
  );
};

export default AllianceParams;
